import React, { useState, useEffect } from 'react';
import { createBeetzeeTransaction, checkBeetzeeTransactionStatus, formatAmount, generateBeetzeeSignature } from '../services/beetzeeService';
import '../css/paymentModal.css';

const PaymentModal = ({ 
  isOpen, 
  onClose, 
  bookingData, 
  totalAmount, 
  onPaymentSuccess,
  onPaymentFailure,
  onPaymentError 
}) => {
  const [paymentStep, setPaymentStep] = useState('initiate'); // initiate, processing, success, error
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [transactionData, setTransactionData] = useState(null);
  const [paymentStatusChecking, setPaymentStatusChecking] = useState(false);
  const [paymentWindow, setPaymentWindow] = useState(null);

  const validateBookingData = () => {
    // Validate required fields
    if (!bookingData.name || bookingData.name.trim() === '') {
      return 'Customer name is required';
    }

    if (!bookingData.email || bookingData.email.trim() === '') {
      return 'Email address is required';
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(bookingData.email)) {
      return 'Please enter a valid email address';
    }

    if (!bookingData.phone || bookingData.phone.trim() === '') {
      return 'Phone number is required';
    }

    if (!bookingData.boat || bookingData.boat.trim() === '') {
      return 'Service selection is required';
    }

    if (!bookingData.date) {
      return 'Booking date is required';
    }

    // Validate date is not in the past
    const selectedDate = new Date(bookingData.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      return 'Booking date cannot be in the past';
    }

    // Validate total amount
    if (!totalAmount || parseFloat(totalAmount) <= 0) {
      return 'Invalid service amount. Please select a valid service';
    }

    return null; // No validation errors
  };

  const handleInitiatePayment = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Validate required booking data before proceeding
      const validationError = validateBookingData();
      if (validationError) {
        throw new Error(validationError);
      }

      // Split name into first and last name
      const nameParts = (bookingData.name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Generate unique merchant reference code
      const merchantRefCode = `BK-${Date.now()}-${Math.random().toString(36).substring(2, 8)}-${bookingData.bookingId || 'TEMP'}`;

      // Prepare URLs (you may want to make these configurable)
      const baseUrl = process.env.REACT_APP_API_URL;
      const webhookUrl = `${baseUrl}/api/beetzee/webhook`;
      const successRedirectUrl = `${baseUrl}/api/beetzee/payment/success`;
      const failedRedirectUrl = `${baseUrl}/api/beetzee/payment/failed`;
      const cancelRedirectUrl = `${baseUrl}/api/beetzee/payment/cancelled`;

      // Prepare transaction data for signature generation
      const parsedAmount = parseFloat(totalAmount);
      if (!parsedAmount || parsedAmount <= 0) {
        throw new Error(`Invalid total amount: ${totalAmount}. Please ensure a service is selected and has a valid price.`);
      }

      const transactionParams = {
        merchant_ref_code: merchantRefCode,
        amount: parsedAmount,
        first_name: firstName,
        last_name: lastName,
        mobile: bookingData.phone || '',
        email: bookingData.email || '',
        webhook_url: webhookUrl,
        success_redirect_url: successRedirectUrl,
        failed_redirect_url: failedRedirectUrl,
        cancel_redirect_url: cancelRedirectUrl,
        expires_in_minutes: 15,
        link_expires_in_minutes:15
      };

      // Generate signature using reference code and amount
      // Secret key will be retrieved from server environment
      const signature = await generateBeetzeeSignature(null, merchantRefCode, parsedAmount);

      // Verify the signature before proceeding
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      
      const verifyResponse = await fetch(`${API_BASE_URL}/api/beetzee/verify-signature`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          signature: signature,
          merchant_ref_code: merchantRefCode,
          amount: parsedAmount
        })
      });

      const verifyResult = await verifyResponse.json();

      if (!verifyResult.success || !verifyResult.signature_valid) {
        throw new Error('Generated signature verification failed. Cannot proceed with payment.');
      }

      // Create transaction data for Beetzee
      const paymentData = {
        ...transactionParams,
        signature
      };

      const result = await createBeetzeeTransaction(paymentData);
      
      if (result.success && result.paymentUrl) {
        setTransactionData(result);
        setPaymentStep('processing');
        
        // Open payment URL in new tab
        const newWindow = window.open(result.paymentUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
        setPaymentWindow(newWindow);
        
        // Monitor tab closure and start payment checking
        startPaymentMonitoring(result.transactionId, newWindow);
      } else {
        setError(result.error || 'Failed to create payment transaction');
        setPaymentStep('error');
      }
    } catch (err) {
      setError(err.message || 'Payment initialization failed');
      setPaymentStep('error');
    } finally {
      setIsLoading(false);
    }
  };

  const startPaymentMonitoring = (transactionId, paymentWindow) => {
    setPaymentStatusChecking(true);
    
    // Monitor if the payment window is closed
    const checkWindowClosure = () => {
      if (paymentWindow && paymentWindow.closed) {
        checkPaymentStatus();
        return;
      }
      
      // Continue checking if window is still open
      if (paymentStatusChecking && paymentWindow && !paymentWindow.closed) {
        setTimeout(checkWindowClosure, 1000); // Check every second
      }
    };
    
    const checkPaymentStatus = async () => {
      try {
        const statusResult = await checkBeetzeeTransactionStatus(transactionId);
        
        if (statusResult.success && statusResult.paid) {
          setPaymentStep('success');
          setPaymentStatusChecking(false);
          
          if (onPaymentSuccess) {
            onPaymentSuccess({
              transactionId,
              amount: statusResult.amount,
              currency: statusResult.currency,
              paidAt: statusResult.paidAt
            });
          }
          return;
        }
        
        // If payment not confirmed yet, wait a bit and check again
        if (paymentStatusChecking) {
          setTimeout(checkPaymentStatus, 3000);
        }
      } catch (err) {
        console.error('Payment status check error:', err);
        // Continue checking despite errors
        if (paymentStatusChecking) {
          setTimeout(checkPaymentStatus, 5000);
        }
      }
    };

    // Start monitoring window closure
    setTimeout(checkWindowClosure, 2000); // Start after 2 seconds
  };

  const handleClose = () => {
    setPaymentStatusChecking(false);
    
    // Close payment window if it's still open
    if (paymentWindow && !paymentWindow.closed) {
      paymentWindow.close();
    }
    setPaymentWindow(null);
    
    setPaymentStep('initiate');
    setTransactionData(null);
    setError('');
    onClose();
  };

  const openPaymentUrl = () => {
    if (transactionData?.paymentUrl) {
      // If there's already a payment window open, focus on it instead of opening a new one
      if (paymentWindow && !paymentWindow.closed) {
        paymentWindow.focus();
      } else {
        const newWindow = window.open(transactionData.paymentUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
        setPaymentWindow(newWindow);
      }
    }
  };

  // Listen for payment status messages from payment window
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'PAYMENT_SUCCESS') {
        console.log('Payment successful:', event.data);
        setPaymentStatusChecking(false);
        if (paymentWindow && !paymentWindow.closed) {
          paymentWindow.close();
        }
        setPaymentWindow(null);
        onPaymentSuccess?.({
          status: 'success',
          transactionData: event.data.data
        });
        handleClose();
      } else if (event.data.type === 'PAYMENT_FAILED') {
        console.log('Payment failed:', event.data);
        setPaymentStatusChecking(false);
        if (paymentWindow && !paymentWindow.closed) {
          paymentWindow.close();
        }
        setPaymentWindow(null);
        onPaymentFailure?.({
          status: 'failed',
          error: event.data.error
        });
        handleClose();
      } else if (event.data.type === 'PAYMENT_CANCELLED') {
        console.log('Payment cancelled:', event.data);
        setPaymentStatusChecking(false);
        if (paymentWindow && !paymentWindow.closed) {
          paymentWindow.close();
        }
        setPaymentWindow(null);
        // Reset to initiate step to allow retry (don't close modal automatically)
        setPaymentStep('cancelled');
        setError('Payment was cancelled. You can try again if needed.');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [paymentWindow, onPaymentSuccess, onPaymentFailure]);

  useEffect(() => {
    return () => {
      setPaymentStatusChecking(false);
      // Clean up payment window on component unmount
      if (paymentWindow && !paymentWindow.closed) {
        paymentWindow.close();
      }
    };
  }, [paymentWindow]);

  if (!isOpen) return null;

  return (
    <div className="payment-modal-overlay">
      <div className="payment-modal">
        <div className="payment-modal-header">
          <h2>Complete Your Payment</h2>
          <button className="close-btn" onClick={handleClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="payment-modal-content">
          {/* Booking Summary */}
          <div className="booking-summary-section">
            <h3>Booking Summary</h3>
            <div className="summary-details">
              <div className="summary-row">
                <span>Service:</span>
                <span>{bookingData.boat}</span>
              </div>
              <div className="summary-row">
                <span>Date:</span>
                <span>{bookingData.date ? new Date(bookingData.date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : 'Date not selected'}</span>
              </div>
              <div className="summary-row total">
                <span>Total Amount:</span>
                <div className="amount-display">
                  <span className="php-amount">{formatAmount(totalAmount, 'PHP')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Steps */}
          {paymentStep === 'initiate' && (
            <div className="payment-step">
              <div className="step-icon">
                <i className="fas fa-credit-card"></i>
              </div>
              <h3>Ready to Pay</h3>
              <p>Click the button below to generate your secure payment link with Beetzee.</p>
              <button 
                className="payment-btn primary"
                onClick={handleInitiatePayment}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Creating Payment Link...
                  </>
                ) : (
                  <>
                    <i className="fas fa-lock"></i>
                    Pay Now
                  </>
                )}
              </button>
            </div>
          )}

          {paymentStep === 'processing' && transactionData && (
            <div className="payment-step">
              <div className="step-icon processing">
                <i className="fas fa-external-link-alt"></i>
              </div>
              <h3>Payment Link Generated</h3>
              <p>Your payment link has been created. Complete the payment to confirm your booking.</p>
              
              <div className="payment-actions">
                <button 
                  className="payment-btn primary"
                  onClick={openPaymentUrl}
                >
                  <i className="fas fa-external-link-alt"></i>
                  Open Payment Page
                </button>
                <button 
                  className="payment-btn tertiary"
                  onClick={handleClose}
                >
                  <i className="fas fa-times"></i>
                  Cancel
                </button>
              </div>

              <div className="status-indicator">
                <i className="fas fa-spinner fa-spin"></i>
                <span>Waiting for payment confirmation...</span>
              </div>
            </div>
          )}

          {paymentStep === 'success' && (
            <div className="payment-step">
              <div className="step-icon success">
                <i className="fas fa-check-circle"></i>
              </div>
              <h3>Payment Successful!</h3>
              <p>Your booking has been confirmed. You will receive a confirmation email shortly.</p>
              <button 
                className="payment-btn success"
                onClick={handleClose}
              >
                <i className="fas fa-check"></i>
                Complete Booking
              </button>
            </div>
          )}

          {paymentStep === 'cancelled' && (
            <div className="payment-step">
              <div className="step-icon cancelled">
                <i className="fas fa-ban"></i>
              </div>
              <h3>Payment Cancelled</h3>
              <p className="cancelled-message">{error}</p>
              <div className="cancelled-actions">
                <button 
                  className="payment-btn primary"
                  onClick={() => {
                    setPaymentStep('initiate');
                    setError('');
                  }}
                >
                  <i className="fas fa-redo"></i>
                  Try Payment Again
                </button>
                <button 
                  className="payment-btn tertiary"
                  onClick={handleClose}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {paymentStep === 'error' && (
            <div className="payment-step">
              <div className="step-icon error">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <h3>Payment Error</h3>
              <p className="error-message">{error}</p>
              <div className="error-actions">
                <button 
                  className="payment-btn secondary"
                  onClick={() => setPaymentStep('initiate')}
                >
                  <i className="fas fa-redo"></i>
                  Try Again
                </button>
                <button 
                  className="payment-btn tertiary"
                  onClick={handleClose}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="payment-modal-footer">
          <div className="security-info">
            <i className="fas fa-shield-alt"></i>
            <span>Payments secured by Beetzee</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;